package com.advaita.cloud.drive.ui.components

import androidx.compose.runtime.Composable
import com.advaita.cloud.drive.shared.AdmissionDto
import com.advaita.cloud.drive.shared.UserInfoDto
import com.advaita.cloud.drive.ui.api.isCapabilityDenied
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

/** ADV-1326c: the page that holds a person out of DataHub, from `/api/me` `admission.status`. */
enum class AdmissionHold {
    /** `PENDING`: the org gates admission, and an org admin has not decided yet. */
    PENDING,

    /** `DENIED` or `REMOVED`: an org admin held the person out, or removed their access. */
    NO_ACCESS,
}

/** The words of one [AdmissionHold] page. */
class AdmissionPageText(val heading: String, val body: String)

/**
 * The page [me] must see instead of the file browser, or null for the file browser. Only
 * `PENDING`, `DENIED` and `REMOVED` hold a person. Null (no standing known: the first page load,
 * an outage, the checks off), `ADMITTED` (a guest too) and a status this frontend does not know
 * keep the page as it was before admission. The vault refuses what the person may not do in
 * every case, so this page only explains it.
 */
fun admissionHoldOf(me: UserInfoDto?): AdmissionHold? = when (me?.admission?.status) {
    AdmissionDto.PENDING -> AdmissionHold.PENDING
    AdmissionDto.DENIED, AdmissionDto.REMOVED -> AdmissionHold.NO_ACCESS
    else -> null
}

/**
 * ADV-1326c (RULING 2026-09-28 (1)): true when the web asks `/api/me/admission` once after [me].
 * The relay reports a sign-in in the background, so a first `/api/me` answers `admission: null`,
 * and the answer can land a moment later. Only a person whose org is from the az2 claim can get
 * a standing. Example: Sam's first `/api/me` answers null, so the web asks once. Gil signs in
 * with gmail, and the relay never reports him, so the web does not ask.
 */
fun admissionPollWanted(me: UserInfoDto?): Boolean =
    me != null && me.admission == null && me.orgSource == UserInfoDto.ORG_CLAIM

/**
 * The user the store holds after the poll answered [polled] for [me], or null to change nothing.
 * Only a hold ([admissionHoldOf]) changes the page. Null, `ADMITTED` and a status this frontend
 * does not know change nothing, because the page is already the one for "no standing known".
 * A poll answer never replaces a standing that `/api/me` gave.
 */
fun afterAdmissionPoll(me: UserInfoDto?, polled: AdmissionDto?): UserInfoDto? {
    if (me == null || me.admission != null || polled == null) return null
    return me.copy(admission = polled).takeIf { admissionHoldOf(it) != null }
}

/**
 * ADV-1326c (ruling 6): true when a creation refused with [refusal] earns one more poll of
 * `/api/me/admission`, with no delay. Only `403 capability_denied` does, and only for a person
 * whose org is from the az2 claim and who is not held yet. The gate's own re-ask on the refused
 * request has just stored the standing on that pod, so this poll finds it. Example: Sam's first
 * poll reached a new pod after a crash and answered null. His first upload gets `403`, the
 * poll finds `PENDING`, and he moves to the Pending page. A `share_not_held` 403 polls nothing.
 */
fun refusalPollWanted(me: UserInfoDto?, refusal: Throwable): Boolean =
    me != null && me.orgSource == UserInfoDto.ORG_CLAIM && admissionHoldOf(me) == null && refusal.isCapabilityDenied()

/**
 * The user the store holds after the re-poll of [refusalPollWanted] answered [polled], or null
 * to change nothing. Only a hold changes the page. Unlike [afterAdmissionPoll], a hold replaces a
 * standing that `/api/me` gave: the refusal is newer. Example: Omar removes Kai while Kai's page
 * shows `ADMITTED`. Kai's upload gets `403`, and the poll moves him to the no-access page.
 */
fun afterRefusalPoll(me: UserInfoDto?, polled: AdmissionDto?): UserInfoDto? {
    if (me == null || polled == null) return null
    return me.copy(admission = polled).takeIf { admissionHoldOf(it) != null }
}

fun admissionPageText(hold: AdmissionHold): AdmissionPageText = when (hold) {
    AdmissionHold.PENDING -> AdmissionPageText(
        "Your access to Advaita Data Hub is waiting for approval",
        "An administrator of your organization must approve your access. " +
            "After they approve it, reload this page. The approval can take up to five minutes " +
            "and two reloads to show.",
    )
    AdmissionHold.NO_ACCESS -> AdmissionPageText(
        "You do not have access to Advaita Data Hub",
        "An administrator of your organization controls access to Advaita Data Hub. " +
            "To get access, speak to an administrator of your organization.",
    )
}

/** The Pending page or the no-access page. The header above it keeps Sign out. */
@Composable
fun AdmissionPage(hold: AdmissionHold) {
    val text = admissionPageText(hold)
    Div(attrs = {
        classes("admission-page")
        attr("data-admission", hold.name)
        attr("role", "status")
        style {
            property("padding", "96px 24px")
            property("text-align", "center")
        }
    }) {
        H1(attrs = {
            style {
                property("font-size", "24px")
                property("font-weight", "400")
                property("margin", "0 0 8px 0")
            }
        }) { Text(text.heading) }
        P(attrs = { style { property("color", "#5f6368") } }) { Text(text.body) }
    }
}
